import { User, useConfigCatClient, useFeatureFlag } from 'configcat-react';
import { useMemo } from 'react';
import { getUserProfile } from '../login/loginSlice';
import { type FeatureToggles, MOCK_FT_VALUES } from './FeatureFlags';
import { useAppSelector } from '../setup/hooks';
import { removeOptionality } from './removeOptionality';

const useConfigCatUser = () => {
    const userProfile = useAppSelector(getUserProfile);
    return useMemo(
        () =>
            userProfile
                ? new User(
                      userProfile.sub ?? 'unknown',
                      undefined,
                      userProfile.locale,
                      removeOptionality({
                          account: userProfile.account,
                          client: userProfile.azp,
                          tenant: userProfile.tenant,
                      })
                  )
                : undefined,
        [userProfile]
    );
};

/**
 * based of https://confluence.collaboration-man.com/display/RTPD/ConfigCat
 */
export const useFeatureToggle = <Name extends keyof FeatureToggles>(
    featureName: Name,
    defaultValue: FeatureToggles[Name]
): FeatureToggles[Name] => {
    const userObject = useConfigCatUser();
    const client = useConfigCatClient();
    const result = useFeatureFlag(featureName, defaultValue, userObject);

    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
        client.setOffline();
        return MOCK_FT_VALUES[featureName];
    }
    return (result.value as FeatureToggles[Name]) ?? defaultValue;
};

export const useFeatureToggleWithLoading = <Name extends keyof FeatureToggles>(
    featureName: Name,
    defaultValue: FeatureToggles[Name]
): { value: FeatureToggles[Name]; loading: boolean } => {
    const userObject = useConfigCatUser();
    const client = useConfigCatClient();
    const result = useFeatureFlag(featureName, defaultValue, userObject);

    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
        client.setOffline();
        return { value: MOCK_FT_VALUES[featureName], loading: false };
    }
    return { value: (result.value as FeatureToggles[Name]) ?? defaultValue, loading: result.loading };
};
